import { gql, Resolver } from '@apollo/client';
import axios from 'axios';
import HttpStatus from 'http-status-codes';

/**
 * @typedef {UpdateDetectionStatusesArgs}
 * @property {string} token a Cognito token
 * @property {string} trqkDirectoryUrl URL for the Trqk Directory API
 * @property {ManageDetectionsUpdates[]} updates the list of updates to be made
 */
interface UpdateDetectionStatusesArgs {
    token: string;
    trqkDirectoryUrl: string;
    updates: ManageDetectionsUpdate[];
}

interface Result {
    results: PromiseSettledResult<Maybe<TrqkAudio>>[];
}

/**
 * Updates the detection_status for one or more `trqkAudio`s.
 * @param {UpdateDetectionStatusesArgs} args
 */
export const updateDetectionStatuses: Resolver = async (
    obj: Record<string, unknown>,
    args: UpdateDetectionStatusesArgs,
): Promise<Result> => {
    const { token, trqkDirectoryUrl, updates } = args;

    const getUpdatePromise = ({
        id,
        statusChange,
    }: ManageDetectionsUpdate): Promise<Maybe<TrqkAudio>> =>
        new Promise((resolve, reject) => {
            axios({
                method: 'post',
                url: trqkDirectoryUrl,
                headers: {
                    Authorization: token,
                },
                // throw an error for non-200 status
                validateStatus: (status: number): boolean =>
                    status === HttpStatus.OK,
                data: {
                    query: `
                        mutation updateTrqkAudio($input: UpdateTrqkAudioInput!) {
                            updateTrqkAudio(input: $input) {
                                id
                                detection_status
                                detection_history {
                                    timestamp
                                    status
                                    source
                                }
                            }
                        }
                    `,
                    variables: {
                        input: {
                            id,
                            detection_status: statusChange.status,
                        },
                    },
                },
            })
                .then(result => {
                    const mutationData: Maybe<TrqkAudio> =
                        result?.data?.data?.updateTrqkAudio ?? null;

                    const errors = result?.data?.errors ?? null;

                    if (errors !== null) {
                        if (errors.length > 1) {
                            throw new Error('GraphQL response contains errors');
                        }

                        throw new Error(
                            errors?.[0]?.message ??
                                'GraphQL response contains an error',
                        );
                    }

                    resolve(mutationData);
                })
                .catch((err: Error) => {
                    reject(new Error(err.message));
                });
        });

    const updatePromises = updates.map(
        (update: ManageDetectionsUpdate): Promise<Maybe<TrqkAudio>> =>
            getUpdatePromise(update),
    );

    const results = await Promise.allSettled(updatePromises);

    return {
        results,
    };
};

export const UPDATE_DETECTION_STATUSES = gql`
    mutation UpdateDetectionStatuses(
        $token: String!
        $trqkDirectoryUrl: String!
        $updates: [ManageDetectionsUpdate]
    ) {
        updateDetectionStatuses(
            token: $token
            trqkDirectoryUrl: $trqkDirectoryUrl
            updates: $updates
        ) @client
    }
`;
